import { DocumentDuplicateIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { InputType } from 'config'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getAdminConfig, getGenerateDocuments, getMlds, setAdminConfig } from 'services'
import { Checkbox, TextArea } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import { MldsDefaultInputs } from './constant'
import LoanBalanceScenariosTable from './LoanBalanceScenariosTable'
import PaymentScenariosTable from './PaymentScenariosTable'
import PrincipalInterestTable from './PrincipalInterestTable'

export const MLDSConfiguration = (props: { setChanged?: Function; loanNumber?: number; update?: Function }) => {
  const [data, setData] = useState<Record<string, any>>({})
  const [isChanged, _setChanged] = useState<boolean>()
  const [isLoading, setLoading] = useState(true)
  const [maturityDate, setMaturityDate] = useState('')
  const inputs: Record<string, InputType> = MldsDefaultInputs()

  const isConfig = !props.loanNumber

  useEffect(() => {
    setLoading(true)
    if (isConfig) {
      getAdminConfig('mlds')
        .then((res) => {
          setData(res)
        })
        .finally(() => setLoading(false))
    } else {
      getMlds()
        .then((res) => {
          setData(res.data)
        })
        .finally(() => setLoading(false))
      getGenerateDocuments().then(({ data }) => {
        setMaturityDate(data.maturityDate)
      })
    }
  }, [])

  const setChanged = (value: boolean) => {
    if (!isConfig) return
    _setChanged(value)
    props.setChanged && props.setChanged(value)
  }

  const handleCheckboxChange = (index: number) => {
    const updatedStatus = [...(data?.principalStatus || [])]
    updatedStatus[index] = !updatedStatus[index]
    setData({ ...data, principalStatus: updatedStatus })
    setChanged(true)
  }
  const handleInputChange = (key: string, value: string | number | any[]) => {
    setChanged(true)

    if (Array.isArray(value)) {
      const numericValues = value.map((v) => (typeof v === 'string' ? parseFloat(v) : v))
      setData((prevData: any) => ({ ...prevData, [key]: numericValues }))
    } else {
      setData({ ...data, [key]: value })
    }
  }

  const onChange = (key: string, value: any) => {
    const temp = cloneDeep(data)
    temp[key] = value
    setData(temp)
    setChanged(true)
    if (key === 'isBalloonPayment') onBlurData(temp)
  }

  const onSave = async () => {
    if (!data) return
    setLoading(true)
    setAdminConfig('mlds', data)
      .then(() => toast('MLDS Configuration has been saved.', { type: 'info' }))
      .finally(() => {
        setLoading(false)
        setChanged(false)
      })
  }

  const onBlur = () => {
    if (!isConfig && props.update) {
      props.update(data)
      setChanged(false)
    }
  }

  const onBlurData = (_data: Record<string, any>) => {
    if (!isConfig && props.update) {
      props.update(_data)
      setChanged(false)
    }
  }

  const pullMaturityDate = () => {
    const temp = cloneDeep(data)
    temp.balloonDueDate = maturityDate
    setData(temp)
    onBlurData(temp)
  }

  return (
    <div>
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} label="Save Changes" onSave={onSave} />

      <div className="grid grid-cols-2 gap-2 mb-4">
        {Object.keys(inputs).map((key) => {
          const input: InputType = inputs[key]
          if (!['section', 'custom'].includes(input.inputType)) input.value = data[key]
          return (
            <div className={`col-span-${input.span || 1}`}>
              <RenderInput input={input} Key={key} key={key} onChange={onChange} onBlur={onBlur} />
            </div>
          )
        })}
      </div>
      <p className="leading-7">
        <span className="font-semibold">XII.</span> The loan is subject to a balloon payment:
        <Checkbox
          id={`isBalloonPayment-no`}
          checked={data.isBalloonPayment === false}
          title="No"
          onChange={() => onChange('isBalloonPayment', false)}
          className={`w-[unset] inline-block mx-2`}
          onBlur={onBlur}
        />
        <Checkbox
          id={`isBalloonPayment-yes`}
          checked={data.isBalloonPayment}
          title="Yes"
          onChange={() => onChange('isBalloonPayment', true)}
          className={`w-[unset] inline-block mx-2`}
          onBlur={onBlur}
        />
        If Yes, the following paragraph applies and a final balloon payment of $
        <input
          value={data.balloonAmount}
          type="number"
          onChange={(event) => onChange('balloonAmount', event.target.value)}
          className="border-b border-b-black mr-2 w-[90px] p-0"
          onBlur={onBlur}
        />
        will be due on
        <input
          type="date"
          value={data.balloonDueDate}
          onChange={(event) => onChange('balloonDueDate', event.target.value)}
          className="border-b border-b-black p-0 ml-2"
          onBlur={onBlur}
        />
        {!isConfig && (
          <span className="inline-block text-shade-blue hover-shadow1 p-1 cursor-pointer" onClick={pullMaturityDate}>
            <DocumentDuplicateIcon className="w-4 h-4" />
          </span>
        )}
      </p>
      <p className="leading-7 mt-8">
        <span className="font-semibold">XIII.</span> Prepayments: The proposed loan has the following prepayment
        provisions:
      </p>
      <p className="leading-7">
        <Checkbox
          id={`noPrepaymentPenalty`}
          checked={data.noPrepaymentPenalty}
          title=""
          onChange={(value) => onChange('noPrepaymentPenalty', value)}
          className={`w-[unset] inline-block mx-2`}
          onBlur={onBlur}
        />
        No prepayment penalty (you will not be charged a penalty to pay off or refinance the loan before maturity)
      </p>
      <p className="leading-7">
        <Checkbox
          id={`havePrepaymentPenalty`}
          checked={data.havePrepaymentPenalty}
          title=""
          onChange={(value) => onChange('havePrepaymentPenalty', value)}
          className={`w-[unset] inline-block mx-2`}
          onBlur={onBlur}
        />
        You will have to pay a prepay mentpenalty if the loanis paid off or refinanced in the first
        <input
          value={data.prepaymentYear}
          type="number"
          onChange={(event) => onChange('prepaymentYear', event.target.value)}
          className="border-b border-b-black mx-2 w-[90px] text-right p-0"
          onBlur={onBlur}
        />
        years. The prepayment penalty could be as much as $
        <input
          value={data.ppAmount}
          type="number"
          onChange={(event) => onChange('ppAmount', event.target.value)}
          className="border-b border-b-black mr-2 w-[90px] p-0"
          onBlur={onBlur}
        />
        . Any prepayment of principal in excess of 20% of the
      </p>
      <p className="pl-24">
        <Checkbox
          id={`isOriginalBalance`}
          checked={data.isOriginalBalance}
          title=""
          onChange={(value) => onChange('isOriginalBalance', value)}
          className={`w-[unset] inline-block mx-2`}
          onBlur={onBlur}
        />
        original loan balance or
      </p>
      <p className="pl-24">
        <Checkbox
          id={`isUnpaidBalance`}
          checked={data.isUnpaidBalance}
          title=""
          onChange={(value) => onChange('isUnpaidBalance', value)}
          className={`w-[unset] inline-block mx-2`}
          onBlur={onBlur}
        />
        unpaid balance
      </p>

      <p className="leading-7">
        for the first
        <input
          value={data.firstPrepaymentYear}
          type="number"
          onChange={(event) => onChange('firstPrepaymentYear', event.target.value)}
          className="border-b border-b-black mx-2 w-[90px] text-right p-0"
          onBlur={onBlur}
        />
        years will include a penalty not to exceed
        <input
          value={data.ppInterestMonth}
          type="number"
          onChange={(event) => onChange('ppInterestMonth', event.target.value)}
          className="border-b border-b-black mx-2 w-[90px] p-0"
          onBlur={onBlur}
        />
        months interest at the note interest rate but not more than the interest you would be charged if the loan were
        paid to maturity.
      </p>
      <p className="leading-7">
        <Checkbox
          id={`otherPrepaymentPenalty`}
          checked={data.otherPrepaymentPenalty}
          title=""
          onChange={(value) => onChange('otherPrepaymentPenalty', value)}
          className={`w-[unset] inline-block mx-2`}
          onBlur={onBlur}
        />
        Other – you will have to pay a prepayment penalty if the loan is paid off or refinanced in the first
        <input
          value={data.firstPaidOffYear}
          type="number"
          onChange={(event) => onChange('firstPaidOffYear', event.target.value)}
          className="border-b border-b-black mx-2 w-[90px] text-right p-0"
          onBlur={onBlur}
        />
        years as follows:
        <TextArea
          value={data.ppDesc}
          onChange={(value) => onChange('ppDesc', value)}
          className="w-full"
          onBlur={onBlur}
        />
      </p>
      <p className="leading-7 mt-8">
        <span className="font-semibold">XVII.</span> Article 7 Compliance: If this proposed loan is secured by a first
        deed of trust in a principal amount of less than $30,000 or secured by a junior lien in a principal amount of
        less than $20,000, the undersigned broker certifies that the loan will be made in compliance with Article 7 of
        Chapter 3 of the Real Estate Law.
      </p>
      <p className="leading-7 pl-12">
        A. This loan
        <Checkbox
          id={`brokerControlFund`}
          checked={data.brokerControlFund === 0}
          title="may"
          onChange={() => onChange('brokerControlFund', 0)}
          className={`w-[unset] inline-block mx-2`}
          onBlur={onBlur}
        />
        <Checkbox
          id={`brokerControlFund`}
          checked={data.brokerControlFund === 1}
          title="will"
          onChange={() => onChange('brokerControlFund', 1)}
          className={`w-[unset] inline-block mx-2`}
          onBlur={onBlur}
        />
        <Checkbox
          id={`brokerControlFund`}
          checked={data.brokerControlFund === 2}
          title="will not"
          onChange={() => onChange('brokerControlFund', 2)}
          className={`w-[unset] inline-block mx-2`}
          onBlur={onBlur}
        />
        be made wholly or in part from broker controlled funds as defined in Section may will will 10241(j) of the
        Business and Professions Code
      </p>
      <p className="leading-7 pl-12">
        B. If the broker indicates in the above statement that the loan "may" be made out of broker-controlled funds,
        the broker must inform the borrower prior to the close of escrow if the funds to be received by the borrower are
        in fact broker-controlled funds
      </p>
      <p className="leading-7 my-8">
        <span className="font-semibold">XVIII.</span> This loan is based on limited or no documentation of your income
        and/or assets and may have a higher interest rate, or more points or fees than other products requiring
        documentation:
        <Checkbox
          id={`docStatus-no`}
          checked={data.docStatus === false}
          title="No"
          onChange={() => onChange('docStatus', false)}
          className={`w-[unset] inline-block mx-2`}
          onBlur={onBlur}
        />
        <Checkbox
          id={`docStatus-yes`}
          checked={data.docStatus}
          title="Yes"
          onChange={() => onChange('docStatus', true)}
          className={`w-[unset] inline-block mx-2`}
          onBlur={onBlur}
        />
      </p>

      <div className="flex flex-col gap-4">
        <PrincipalInterestTable
          data={data}
          handleCheckboxChange={handleCheckboxChange}
          handleInputChange={handleInputChange}
          onBlur={onBlur}
        />
        <PaymentScenariosTable data={data} handleInputChange={handleInputChange} onBlur={onBlur} />
        <LoanBalanceScenariosTable data={data} handleInputChange={handleInputChange} onBlur={onBlur} />
      </div>
      <p className="leading-7 my-8">
        <span className="font-semibold">*.</span> "Not offered" indicates the broker does not offer the comparison loan
        product. The information provided for the products not offered was obtained from sources deemed reliable
        <Checkbox
          id={`offerStatus-yes`}
          checked={data.offerStatus}
          title="Yes"
          onChange={() => onChange('offerStatus', true)}
          className={`w-[unset] inline-block mx-2`}
          onBlur={onBlur}
        />
        <Checkbox
          id={`offerStatus-no`}
          checked={data.offerStatus === false}
          title="No"
          onChange={() => onChange('offerStatus', false)}
          className={`w-[unset] inline-block mx-2`}
          onBlur={onBlur}
        />
      </p>
    </div>
  )
}
